import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import { useToast } from '../components/ui/use-toast';
import { apiRequest } from '../config/api';

const HospitalAuthContext = createContext(undefined);

export const HospitalAuthProvider = ({ children }) => {
  const { toast } = useToast();

  const [hospitalUser, setHospitalUser] = useState(null);
  const [hospital, setHospital] = useState(null);
  const [loading, setLoading] = useState(true);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);

  // Check for existing hospital session on mount
  useEffect(() => {
    const checkSession = async () => {
      const token = localStorage.getItem('hospital_session_token');
      const storedHospital = localStorage.getItem('hospital_data');
      const storedUser = localStorage.getItem('hospital_user');
      
      if (token && storedHospital && storedUser) {
        try {
          const hospitalData = JSON.parse(storedHospital);
          
          // Verify hospital status
          const statusData = await apiRequest(`get_hospital_status.php?hospital_id=${hospitalData.id}`);
          
          if (statusData.success) {
            setHospital(statusData.hospital);
            setSubscriptionStatus(statusData.subscription);
            setHospitalUser(JSON.parse(storedUser));
          } else {
            // Status check failed, clear session
            localStorage.removeItem('hospital_session_token');
            localStorage.removeItem('hospital_data');
            localStorage.removeItem('hospital_user');
            setHospital(null);
            setHospitalUser(null);
            setSubscriptionStatus(null);
          }
        } catch (error) {
          console.error('Hospital session check error:', error);
          localStorage.removeItem('hospital_session_token');
          localStorage.removeItem('hospital_data');
          localStorage.removeItem('hospital_user');
          setHospital(null);
          setHospitalUser(null);
          setSubscriptionStatus(null);
        }
      }
      setLoading(false);
    };

    checkSession();
  }, []);

  const hospitalSignIn = useCallback(async (email, password) => {
    try {
      const data = await apiRequest('hospital_login.php', {
        method: 'POST',
        body: JSON.stringify({
          email: email,
          password: password
        })
      });

      if (data.success) {
        // Store session
        localStorage.setItem('hospital_session_token', data.session_token);
        localStorage.setItem('hospital_user', JSON.stringify(data.user));
        localStorage.setItem('hospital_data', JSON.stringify(data.hospital));
        
        setHospitalUser(data.user);
        setHospital(data.hospital);
        setSubscriptionStatus(data.hospital.subscription_end ? {
          active: true,
          end_at: data.hospital.subscription_end
        } : {
          active: true,
          end_at: data.hospital.trial_end_at
        });
        
        return { error: null };
      } else {
        throw new Error(data.error || data.message || 'Login failed');
      }
    } catch (error) {
      console.error('Hospital login error:', error);
      localStorage.removeItem('hospital_session_token');
      localStorage.removeItem('hospital_data');
      localStorage.removeItem('hospital_user');
      setHospital(null);
      setHospitalUser(null);
      setSubscriptionStatus(null);
      return { error };
    }
  }, []);

  const hospitalSignOut = useCallback(async () => {
    localStorage.removeItem('hospital_session_token');
    localStorage.removeItem('hospital_data');
    localStorage.removeItem('hospital_user');
    setHospital(null);
    setHospitalUser(null);
    setSubscriptionStatus(null);
  }, []);

  const refreshHospitalStatus = useCallback(async () => {
    if (!hospital) return;
    
    try {
      const statusData = await apiRequest(`get_hospital_status.php?hospital_id=${hospital.id}`);
      
      if (statusData.success) {
        setHospital(statusData.hospital);
        setSubscriptionStatus(statusData.subscription);
        
        // Update localStorage
        localStorage.setItem('hospital_data', JSON.stringify(statusData.hospital));
      }
    } catch (error) {
      console.error('Failed to refresh hospital status:', error);
    }
  }, [hospital]);

  const value = useMemo(
    () => ({
      hospitalUser,
      hospital,
      subscriptionStatus,
      loading,
      hospitalSignIn,
      hospitalSignOut,
      refreshHospitalStatus,
      isApproved: hospital?.status === 'APPROVED',
      hasActiveSubscription: subscriptionStatus?.active === true
    }),
    [hospitalUser, hospital, subscriptionStatus, loading, hospitalSignIn, hospitalSignOut, refreshHospitalStatus]
  );

  return <HospitalAuthContext.Provider value={value}>{children}</HospitalAuthContext.Provider>;
};

export const useHospitalAuth = () => {
  const context = useContext(HospitalAuthContext);
  if (context === undefined) {
    throw new Error('useHospitalAuth must be used within a HospitalAuthProvider');
  }
  return context;
};

